import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Activity, Briefcase } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { formatTime, formatRelativeTime, formatDate } from '@/lib/utils/date';
import { useMDTeamActivity } from '../hooks/useMDTeamActivity';
import type { AttendanceState, MemberAttendance, TeamActivityMember } from '../api/dashboard.api';

// ──────────────────────────────────────────────────────────────────────────
// Live "now" tick — re-renders every second so running timers count up.
// ──────────────────────────────────────────────────────────────────────────
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// Avatar accent per role type — quiet tints, just enough to tell roles apart.
const ROLE_TONE: Record<TeamActivityMember['roleType'], string> = {
  MD: 'bg-violet-100 text-violet-700',
  PARTNER: 'bg-sky-100 text-sky-700',
  LEAD: 'bg-amber-100 text-amber-700',
  STAFF: 'bg-slate-100 text-slate-600',
};

// State carried by the dot only — text stays muted so colour reads as a signal.
const ATTENDANCE_DOT: Record<AttendanceState, string> = {
  IN_OFFICE: 'bg-emerald-500',
  LEFT: 'bg-slate-300',
  ABSENT: 'bg-amber-400',
};

/**
 * Office presence as quiet plain text — "· In office 9:12" / "· Left 18:04" / "· Not in".
 * No pill, no ring; a tiny dot carries the state, the words stay subtle.
 */
function AttendanceText({ attendance }: { attendance: MemberAttendance }) {
  const { t } = useTranslation('common');
  const label =
    attendance.state === 'IN_OFFICE'
      ? t('dashboard.teamActivity.inOffice')
      : attendance.state === 'LEFT'
        ? t('dashboard.teamActivity.left')
        : t('dashboard.teamActivity.notIn');
  const time =
    attendance.state === 'IN_OFFICE'
      ? attendance.arrivedAt
      : attendance.state === 'LEFT'
        ? attendance.leavedAt
        : null;

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-slate-400">
      <span className={`h-1.5 w-1.5 rounded-full ${ATTENDANCE_DOT[attendance.state]}`} />
      <span>{label}</span>
      {time ? <span className="tabular-nums">{formatDate(time, 'HH:mm')}</span> : null}
    </span>
  );
}

/** A working member: who, the work line, and a calm running timer. No coloured surface. */
function WorkingRow({ member, now }: { member: TeamActivityMember; now: number }) {
  const { t } = useTranslation('common');
  const a = member.current!;
  const elapsed = Math.max(0, Math.floor((now - new Date(a.startedAt).getTime()) / 1000));
  const activityLabel = a.activityDescription || a.activityType;

  return (
    <li className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-slate-50">
      <div className="relative shrink-0">
        <Avatar className="h-9 w-9 ring-1 ring-slate-200/70">
          <AvatarFallback className={`text-[0.8125rem] font-semibold ${ROLE_TONE[member.roleType]}`}>
            {initials(member.name)}
          </AvatarFallback>
        </Avatar>
        {/* Single quiet presence dot — the one accent that marks an active person. */}
        <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500" />
      </div>

      <div className="min-w-0 flex-1">
        {/* Person + status (subtle) */}
        <div className="flex items-center gap-2">
          <Link
            to={`/app/users/${member.userId}`}
            className="truncate text-[0.9375rem] font-semibold text-slate-900 hover:text-[#0069D1] hover:underline"
          >
            {member.name}
          </Link>
          {member.roleName ? (
            <span className="shrink-0 text-xs text-slate-400">{member.roleName}</span>
          ) : null}
          <AttendanceText attendance={member.attendance} />
        </div>

        {/* Task — the headline of what they're doing */}
        <p className="mt-0.5 truncate text-[0.875rem] font-medium text-slate-800">
          {a.taskId != null && a.taskTitle ? (
            <Link to={`/app/new-tasks/${a.taskId}`} className="hover:text-[#0069D1] hover:underline">
              {a.taskTitle}
            </Link>
          ) : (
            <span className="text-slate-400">{activityLabel ?? t('dashboard.teamActivity.noTask')}</span>
          )}
        </p>

        {/* Matter · activity — tertiary, plain text */}
        {a.matterId != null && a.matterName ? (
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[0.8125rem] text-slate-500">
            <Briefcase size={13} className="shrink-0 text-slate-400" />
            <Link to={`/app/matters/${a.matterId}`} className="truncate hover:text-[#0069D1] hover:underline">
              {a.matterName}
            </Link>
            {activityLabel ? (
              <>
                <span className="text-slate-300">·</span>
                <span className="truncate text-slate-400">{activityLabel}</span>
              </>
            ) : null}
          </div>
        ) : activityLabel ? (
          <div className="mt-0.5 truncate text-[0.8125rem] text-slate-400">{activityLabel}</div>
        ) : null}
      </div>

      {/* Running timer — calm, elegant, the one figure on the right */}
      <span className="shrink-0 font-mono text-[0.9375rem] font-semibold tabular-nums text-emerald-600">
        {formatTime(elapsed)}
      </span>
    </li>
  );
}

/** An idle member: lighter, muted; shows the last thing they touched. */
function IdleRow({ member }: { member: TeamActivityMember }) {
  const { t } = useTranslation('common');
  const last = member.last;

  return (
    <li className="flex items-center gap-3 px-2 py-1.5">
      <Avatar className="h-8 w-8 shrink-0 opacity-90">
        <AvatarFallback className="bg-slate-100 text-[0.75rem] font-semibold text-slate-400">
          {initials(member.name)}
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/app/users/${member.userId}`}
            className="truncate text-[0.875rem] font-medium text-slate-600 hover:text-[#0069D1] hover:underline"
          >
            {member.name}
          </Link>
          {member.roleName ? (
            <span className="shrink-0 text-xs text-slate-400">{member.roleName}</span>
          ) : null}
          <AttendanceText attendance={member.attendance} />
        </div>
        {last ? (
          <p className="mt-0.5 truncate text-[0.8125rem] text-slate-400">
            <span>{t('dashboard.teamActivity.lastWorkedOn')} </span>
            {last.taskId != null && last.taskTitle ? (
              <Link to={`/app/new-tasks/${last.taskId}`} className="text-slate-500 hover:text-[#0069D1] hover:underline">
                {last.taskTitle}
              </Link>
            ) : (
              <span className="text-slate-500">{last.activityDescription || last.activityType}</span>
            )}
            {last.matterName ? <span> · {last.matterName}</span> : null}
          </p>
        ) : (
          <p className="mt-0.5 truncate text-[0.8125rem] text-slate-300">
            {t('dashboard.teamActivity.noRecentActivity')}
          </p>
        )}
      </div>

      {last ? (
        <span className="shrink-0 text-xs text-slate-400">{formatRelativeTime(last.endedAt)}</span>
      ) : null}
    </li>
  );
}

/** Small section label that separates the Working / Idle groups. */
function GroupLabel({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center gap-2 px-2 pb-1.5">
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-[0.6875rem] font-medium tabular-nums text-slate-300">{count}</span>
    </div>
  );
}

/** Team Activity — live view of who is working right now and on what. */
export function TeamActivityCard() {
  const { t } = useTranslation('common');
  const now = useNow();

  const { data, isLoading } = useMDTeamActivity();
  const members = data?.members ?? [];
  const working = members.filter(m => m.current != null).sort((a, b) => a.name.localeCompare(b.name));
  const idle = members.filter(m => m.current == null).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-slate-100">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity size={16} className="text-slate-400" /> {t('dashboard.teamActivity.title')}
            </CardTitle>
            <CardDescription>{t('dashboard.teamActivity.desc')}</CardDescription>
          </div>
          {/* Quiet live count — a small dot as signal, no badge */}
          <span className="inline-flex shrink-0 items-center gap-2 pt-1 text-xs font-medium text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {t('dashboard.teamActivity.workingNow', { count: working.length })}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="h-40 rounded-md bg-slate-100 animate-pulse" />
        ) : members.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">{t('dashboard.teamActivity.empty')}</p>
        ) : (
          <div className="flex flex-col gap-5">
            {working.length > 0 && (
              <div>
                <GroupLabel label={t('dashboard.teamActivity.groupWorking')} count={working.length} />
                <ul className="flex flex-col gap-0.5">
                  {working.map(m => (
                    <WorkingRow key={m.userId} member={m} now={now} />
                  ))}
                </ul>
              </div>
            )}
            {idle.length > 0 && (
              <div>
                <GroupLabel label={t('dashboard.teamActivity.groupIdle')} count={idle.length} />
                <ul className="flex flex-col gap-0.5">
                  {idle.map(m => (
                    <IdleRow key={m.userId} member={m} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
